import type { AccountMinimumItemParams } from "./account-minimum-line";

type MinBuyItemParams = { product: string; min: string; required: string };

// Local translations
export const T = {
  en_GB: {
    saving: "Saving...",
    portfolioName: "Portfolio Name",
    portfolioNamePlaceholder: "e.g., Core Portfolio",
    portfolioType: "Portfolio Type",
    selectPortfolioType: "Select portfolio type",
    modelPortfolio: "Model Portfolio",
    customPortfolio: "Custom Portfolio",
    dynamicPortfolio: "Dynamic Portfolio",
    scheduleType: "Schedule Type",
    selectScheduleType: "Select schedule type",
    scheduleLockedAccount: (source: string) =>
      `This portfolio follows up account ${source}, so it is always a one-time investment.`,
    scheduleLockedSavingsPlan: (source: string) =>
      `This portfolio follows up savings agreement ${source}, so it is always recurring.`,
    oneTimeLumpSum: "One-time (Lump Sum)",
    recurringContributions: "Recurring (Contributions)",
    amount: "Amount",
    monthOptional: "Month (Optional)",
    frequency: "Frequency",
    selectFrequency: "Select frequency",
    monthly: "Monthly",
    quarterly: "Quarterly",
    yearly: "Yearly",
    amountPerMonth: "Amount per month",
    amountPerQuarter: "Amount per quarter",
    amountPerYear: "Amount per year",
    totalAmount: "Total amount",
    perMonth: "per month",
    perQuarter: "per quarter",
    perYear: "per year",
    totalPrefix: "Total:",
    depositsLabel: "Deposits:",
    startMonthOptional: "Start Month (Optional)",
    endMonthOptional: "End Month (Optional)",
    defaultPrefix: "Default:",
    agreementStart: "Agreement's start",
    openEnded: "Open-ended",
    endBeforeStartMessage: "The end month is before the start month",
    error: "Error",
    failedToSave: "Failed to save portfolio",
    incompleteMessage: "Complete portfolio details before submitting",
    minBuyHeading: "Some products in this portfolio are below their minimum buy amount:",
    minBuyItem: (p: MinBuyItemParams) =>
      `${p.product}: minimum ${p.min} per buy — requires a portfolio amount of at least ${p.required}`,
    accountMinimumBlockedMessage:
      "An account this portfolio invests into would hold less than the minimum for its wrapper type. Increase the amounts in the portfolios that invest into it.",
    accountMinimumItem: (p: AccountMinimumItemParams) =>
      `${p.wrapper} account would hold ${p.value} ${p.currency}, below the ${p.minimum} ${p.currency} minimum for this wrapper type (short by ${p.shortfall} ${p.currency}).`,
    dynamicDisabledForCollection: "dynamic portfolios are disabled for this collection",
    // QL-664 — a mandate template governs the goal: custom portfolios only.
    typeRestrictedByMandate: "not available — this goal is checked against a mandate template",
    customMissingUnderMandate:
      "This goal is checked against a mandate template and takes custom portfolios only, but this flow does not offer them. Ask an administrator to allow custom portfolios in the flow's Portfolio input component.",
    customMissingUnderMandatePublic:
      "This portfolio cannot be completed here. Your advisor will set it up for you.",
    switchToCustomForMandate:
      "Switch this portfolio to custom — the goal is checked against a mandate template, which allows custom portfolios only.",
    noModelPortfolioForScoreOnPlatform: (score: string, platform: string) =>
      `No model portfolio is available for risk score ${score} on platform ${platform}.`,
    noModelPortfolioForScore: (score: string) =>
      `No model portfolio is available for risk score ${score}.`,
  },
  nb_NO: {
    saving: "Lagrer...",
    portfolioName: "Porteføljenavn",
    portfolioNamePlaceholder: "f.eks., Kjerneportefølje",
    portfolioType: "Porteføljetype",
    selectPortfolioType: "Velg porteføljetype",
    modelPortfolio: "Modellportefølje",
    customPortfolio: "Tilpasset portefølje",
    dynamicPortfolio: "Dynamisk portefølje",
    scheduleType: "Plantype",
    selectScheduleType: "Velg plantype",
    scheduleLockedAccount: (source: string) =>
      `Denne porteføljen følger opp kontoen ${source}, så den er alltid en engangsinvestering.`,
    scheduleLockedSavingsPlan: (source: string) =>
      `Denne porteføljen følger opp spareavtalen ${source}, så den er alltid løpende.`,
    oneTimeLumpSum: "Engangsbeløp",
    recurringContributions: "Løpende (bidrag)",
    amount: "Beløp",
    monthOptional: "Måned (valgfri)",
    frequency: "Frekvens",
    selectFrequency: "Velg frekvens",
    monthly: "Månedlig",
    quarterly: "Kvartalsvis",
    yearly: "Årlig",
    amountPerMonth: "Beløp per måned",
    amountPerQuarter: "Beløp per kvartal",
    amountPerYear: "Beløp per år",
    totalAmount: "Totalbeløp",
    perMonth: "per måned",
    perQuarter: "per kvartal",
    perYear: "per år",
    totalPrefix: "Totalt:",
    depositsLabel: "Innskudd:",
    startMonthOptional: "Startmåned (valgfri)",
    endMonthOptional: "Sluttmåned (valgfri)",
    defaultPrefix: "Standard:",
    agreementStart: "Avtalens start",
    openEnded: "Uten sluttdato",
    endBeforeStartMessage: "Sluttmåneden er før startmåneden",
    error: "Feil",
    failedToSave: "Kunne ikke lagre portefølje",
    incompleteMessage: "Fyll inn porteføljedetaljer før innsending",
    minBuyHeading: "Noen produkter i denne porteføljen er under sitt minimumsbeløp for kjøp:",
    minBuyItem: (p: MinBuyItemParams) =>
      `${p.product}: minimum ${p.min} per kjøp — krever et porteføljebeløp på minst ${p.required}`,
    accountMinimumBlockedMessage:
      "En konto denne porteføljen investerer i ville hatt mindre enn minimumsverdien for sin innpakningstype. Øk beløpene i porteføljene som investerer i den.",
    accountMinimumItem: (p: AccountMinimumItemParams) =>
      `${p.wrapper}-kontoen ville hatt ${p.value} ${p.currency}, under minimumsverdien på ${p.minimum} ${p.currency} for denne innpakningstypen (mangler ${p.shortfall} ${p.currency}).`,
    dynamicDisabledForCollection: "dynamiske porteføljer er deaktivert for denne samlingen",
    typeRestrictedByMandate: "ikke tilgjengelig — dette målet kontrolleres mot en mandatmal",
    customMissingUnderMandate:
      "Dette målet kontrolleres mot en mandatmal og kan bare ha egendefinerte porteføljer, men denne flyten tilbyr dem ikke. Be en administrator tillate egendefinerte porteføljer i flytens komponent Porteføljevalg.",
    customMissingUnderMandatePublic:
      "Denne porteføljen kan ikke fullføres her. Rådgiveren din setter den opp for deg.",
    switchToCustomForMandate:
      "Bytt denne porteføljen til egendefinert — målet kontrolleres mot en mandatmal, som bare tillater egendefinerte porteføljer.",
    noModelPortfolioForScoreOnPlatform: (score: string, platform: string) =>
      `Ingen modellportefølje er tilgjengelig for risikoscore ${score} på plattformen ${platform}.`,
    noModelPortfolioForScore: (score: string) =>
      `Ingen modellportefølje er tilgjengelig for risikoscore ${score}.`,
  },
} as const;
